import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CANON } from "@/lib/canon.generated";
import { formatDay, formatMediumDay } from "@/lib/date";
import { EXPORT } from "@/lib/export.generated";
import type { PublicTimelapse } from "./api.ts";
import "./film.css";
import { cameToNothing } from "@/lib/Picture";

/*
 * The diary's own cadence, and not a second copy of it.
 *
 * This was `const FPS = 8` — the right number, typed out, next to a
 * generated constant that already held it. `design/tokens.json` carries
 * `timelapseFrameIntervalMs: 125`, and the phone derives its export's
 * slowest rate from that with a comment saying why: "it is the diary's own
 * mini-timelapse cadence, and that number already answers 'when does a
 * sequence stop reading as motion'. Two constants for one question would
 * eventually disagree." The generator writes the same derivation into
 * `EXPORT.frameRateFloor`, whose own line calls it the diary's cadence.
 *
 * So the player reads it. Move the interval in the tokens and the phone's
 * player, the phone's export and this app's export all follow; before this
 * line, this app's player was the only thing in either client that did not.
 */
const FPS = EXPORT.frameRateFloor;

/**
 * How many frames either side of the current one stay mounted once the player
 * is live. Behind is one, because scrubbing back a step should be instant;
 * ahead is larger while playing, since at eight frames a second the browser
 * has an eighth of a second per image to fetch and decode the next one.
 */
const LOOKBEHIND = 1;
const LOOKAHEAD_IDLE = 2;
const LOOKAHEAD_PLAYING = 8;

/**
 * Every frame is a real <img> in the document *as delivered* — that is what
 * makes the page indexable and useful without JavaScript. With JS off the
 * first frame stays on screen and the rest are a plain gallery in flow, each
 * day under the last.
 *
 * Once the player is live, that stops being free. The frames are absolutely
 * positioned on top of one another, so every one of them overlaps the
 * viewport and `loading="lazy"` defers nothing: a year-long timelapse asked
 * the browser for three hundred and sixty-five full-size images at once and
 * decoded them all, which is a stalled page and a large amount of somebody's
 * data for frames they may never reach. After hydration only a window around
 * the current frame stays mounted.
 *
 * Unmounting is not enough, though. The browser starts a load the moment it
 * parses an <img src>, before any JavaScript has run, and removing the
 * element cancels nothing: measured on a 120-day film, all 120 frames were
 * fetched to the end with three of them left in the DOM. So the markup the
 * server sends — and the first client render, which must match it or React
 * discards the markup it is adopting — carries the first frame as an image
 * and the rest inside <noscript>. With scripting on, a browser makes no
 * elements out of a noscript's contents and requests nothing; the effect
 * then mounts the window. With scripting off, the noscript is the gallery,
 * and that reader's browser fetches all of it — lazy loading is off by the
 * letter of the standard when scripting is, so there is nothing to defer
 * with, and a gallery is what they came for.
 */
/**
 * The frames that can actually be shown.
 *
 * A day reaches the response with a null `face_centered_url` when its crop is
 * not attached — processing that half-finished, most often. The player has to
 * skip those, and the page above has to count the same ones, or the line
 * saying how many days there are describes a film nobody is watching. One
 * predicate, so the two cannot drift.
 */
export function playableFrames(items: PublicTimelapse["items"]) {
  return (items ?? []).filter((item) => Boolean(item.face_centered_url));
}

export function TimelapseFilm({
  items,
  overlay,
}: {
  items: PublicTimelapse["items"];
  /** Drawn over the frame's lower edge — the diary's count of days. */
  overlay?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const frames = useMemo(() => playableFrames(items), [items]);
  const hasFrames = frames.length > 0;
  const [index, setIndex] = useState(0);
  const visibleIndex = Math.min(index, Math.max(0, frames.length - 1));
  const [playing, setPlaying] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const figure = useRef<HTMLElement>(null);
  /*
   * Frames whose bytes would not come: a signed address that expired while
   * the film sat open, a connection that went. A broken frame drew the
   * browser's mark and its own alt — "The day of August 1, 2026" — as text
   * over the placeholder, in the middle of the film. Such a frame is kept
   * in the reel, so the scrub still names its day, but the picture on
   * screen is the last one before it that did arrive: a film missing one
   * day out of four hundred is still the film, and a grey flash is not.
   */
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set());
  const markBroken = (id: string) =>
    setBroken((current) => (current.has(id) ? current : new Set(current).add(id)));
  /*
   * And frames whose bytes have not come yet. The reel advances eight times
   * a second whether or not the next frame has arrived, and on a phone's
   * connection it had not: a frame drawn before its bytes is the grey of
   * the placeholder, and a film of a hundred and twenty days played the
   * moment it opened flashed grey for a twelfth of its first six seconds,
   * measured at four hundred milliseconds a frame. A frame that has not
   * arrived is treated as one that will not: the picture on screen is the
   * last one that did, held a beat longer, and the scrub goes on naming
   * the day. A frame held is still the film; a grey flash is not.
   */
  const [arrived, setArrived] = useState<ReadonlySet<string>>(() => new Set());
  const markArrived = (id: string) =>
    setArrived((current) => (current.has(id) ? current : new Set(current).add(id)));

  useEffect(() => setHydrated(true), []);

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(0, frames.length - 1)));
    if (frames.length < 2) setPlaying(false);
  }, [frames.length]);

  useEffect(() => {
    if (!hasFrames) return;
    const pauseWhenHidden = () => {
      if (document.hidden) setPlaying(false);
    };
    document.addEventListener("visibilitychange", pauseWhenHidden);
    const node = figure.current;
    const observer =
      node && typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver((entries) => {
            if (entries.some((entry) => !entry.isIntersecting)) setPlaying(false);
          })
        : null;
    if (node) observer?.observe(node);
    return () => {
      document.removeEventListener("visibilitychange", pauseWhenHidden);
      observer?.disconnect();
    };
  }, [hasFrames]);

  useEffect(() => {
    if (!playing || frames.length < 2) return;
    timer.current = setInterval(() => {
      setIndex((current) => (current + 1) % frames.length);
    }, 1000 / FPS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [playing, frames.length]);

  if (frames.length === 0) return null;

  // The frame drawn for the day the reel is at: itself, or the nearest
  // earlier one that arrived, or the nearest later one when nothing before
  // it did — and the day's own, still on its way, when nothing has.
  const showable = (position: number) => {
    const frame = frames[position];
    return frame !== undefined && arrived.has(frame.id) && !broken.has(frame.id);
  };
  let shownIndex = visibleIndex;
  while (shownIndex > 0 && !showable(shownIndex)) shownIndex -= 1;
  if (!showable(shownIndex)) {
    const first = frames.findIndex((_, position) => showable(position));
    shownIndex = first === -1 ? visibleIndex : first;
  }

  const lookahead = playing ? LOOKAHEAD_PLAYING : LOOKAHEAD_IDLE;
  const isMounted = (position: number) => {
    if (!hydrated) return position === 0;
    // The frame on screen stays, however far behind the reel it is.
    if (position === shownIndex) return true;

    const distance = position - visibleIndex;
    if (distance >= 0 && distance <= lookahead) return true;
    if (distance < 0 && distance >= -LOOKBEHIND) return true;

    // Playback loops, so the frames at the start of the reel are the ones
    // that come next after the end, and they need the same head start.
    return distance + frames.length <= lookahead;
  };

  return (
    <figure ref={figure} className="timelapse-figure flex flex-col gap-3">
      <div
        // Bounded, not merely `w-full` — by the column and by the window's
        // height, see film.css: the scrub under the film has to be on the
        // screen whose whole job is to be scrubbed.
        className="timelapse-frame relative w-full overflow-hidden rounded-hero bg-thumb-placeholder"
        style={{ aspectRatio: `${CANON.aspectRatio[0]} / ${CANON.aspectRatio[1]}` }}
      >
        {frames.map((frame, position) =>
          isMounted(position) ? (
            <img
              key={frame.id}
              src={frame.face_centered_url ?? ""}
              // Only the frame on screen is described. The others are the same
              // face on other days; announcing all of them turns one picture
              // into hundreds of near-identical readings.
              alt={
                position === visibleIndex
                  ? t("public.frameOn", { date: formatDay(frame.taken_on, i18n.language) })
                  : ""
              }
              aria-hidden={position !== visibleIndex}
              loading={position === 0 ? "eager" : "lazy"}
              className="absolute inset-0 size-full object-cover data-[hidden=true]:opacity-0"
              data-hidden={position !== shownIndex}
              // The first frame is the server's, and can fail — or arrive —
              // before the player is attached to it: read off the element
              // then. A frame mounted again from the cache is complete at
              // once, and never fires `load`.
              ref={(node) => {
                if (!node) return;
                if (cameToNothing(node)) markBroken(frame.id);
                else if (node.complete && node.naturalWidth > 0) markArrived(frame.id);
              }}
              onLoad={() => markArrived(frame.id)}
              onError={() => markBroken(frame.id)}
            />
          ) : null,
        )}
        {overlay}
      </div>
      {!hydrated && frames.length > 1 && (
        // The class is `display: contents` only where scripting is off, so
        // that each frame is a row of the figure like the first one, with
        // the same gap between them — see film.css for why it must not be
        // said unconditionally.
        <noscript className="timelapse-gallery">
          {frames.slice(1).map((frame) => (
            <img
              key={frame.id}
              src={frame.face_centered_url ?? ""}
              // Every frame of this gallery is on screen in its turn, so every
              // one is described — unlike the player's hidden neighbours.
              alt={t("public.frameOn", { date: formatDay(frame.taken_on, i18n.language) })}
              className="timelapse-frame w-full rounded-hero bg-thumb-placeholder object-cover"
              style={{ aspectRatio: `${CANON.aspectRatio[0]} / ${CANON.aspectRatio[1]}` }}
            />
          ))}
        </noscript>
      )}
      {frames.length > 1 && (
        // Wrapping, for the one size where the three do not fit on a line:
        // at 200% text on a 320px phone the date refused to shrink and
        // stood seventeen pixels past the page's edge, and the whole page
        // scrolled sideways for it. It takes a line of its own there, on
        // the right; at every other size nothing moves.
        <div className="flex flex-wrap items-center gap-3">
          {/*
            Dead until the script is there, and saying so.

            With scripting off the `noscript` above lays every frame out as
            its own picture, which is the whole gallery — and these two sat
            under it looking live. Pressing play did nothing; the slider's
            thumb moved and nothing followed it. Disabled rather than
            absent, so the row keeps its height and nothing jumps when the
            player wakes up a tick after mounting.
          */}
          <button
            type="button"
            disabled={!hydrated}
            onClick={() => setPlaying((value) => !value)}
            aria-label={playing ? t("public.pause") : t("public.play")}
            title={playing ? t("public.pause") : t("public.play")}
            aria-pressed={playing}
            className="film-play rounded-cta border border-hairline px-4 py-2 text-sm"
          >
            <span aria-hidden="true">{playing ? "❚❚" : "▶"}</span>
          </button>
          <input
            type="range"
            disabled={!hydrated}
            min={0}
            max={frames.length - 1}
            value={visibleIndex}
            onChange={(event) => {
              setPlaying(false);
              setIndex(Number(event.target.value));
            }}
            // The label says what the control does; the date is its value.
            // Labelled with the date, a screen reader announced "2026-08-01,
            // slider" — the reading tells you where you are and nothing about
            // what moving it would do.
            aria-label={t("public.scrub")}
            aria-valuetext={
              frames[visibleIndex]
                ? formatDay(frames[visibleIndex].taken_on, i18n.language)
                : undefined
            }
            className="min-w-0 flex-1"
          />
          {/*
            The slider one element to the left already announces this day as
            "August 1, 2026"; printing `2026-08-01` beside it gave the reader
            and the listener two different spellings of the same value. The
            abbreviated month is what fits: this row holds the control being
            dragged, and a date that grows from "May" to "September" would
            resize it mid-drag. `dateTime` keeps the machine's copy machine-
            readable.
          */}
          <time
            className="ml-auto shrink-0 text-dim text-sm tabular-nums"
            dateTime={frames[visibleIndex]?.taken_on}
          >
            {frames[visibleIndex]
              ? formatMediumDay(frames[visibleIndex].taken_on, i18n.language)
              : null}
          </time>
        </div>
      )}
    </figure>
  );
}
